import React from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Typography, Box, Chip, Stack, IconButton, Divider,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import StatusChip from "./_StatusChip";

/** Side-drawer-style dialog showing the full JSON payload for one log row. */
export default function LogDetailDialog({ row, onClose }) {
  if (!row) return null;

  const copy = (data) => {
    try {
      navigator.clipboard.writeText(typeof data === "string" ? data : JSON.stringify(data, null, 2));
    } catch (_) {/* ignore */}
  };

  return (
    <Dialog
      open={!!row}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          borderLeft: "5px solid",
          borderImage: "linear-gradient(180deg, #007C84, #016268) 1",
        },
      }}
    >
      <DialogTitle
        sx={{
          background: "linear-gradient(135deg, #E0F4F5, #F0FAFA)",
          borderBottom: "1px solid #E5E7EB",
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" fontWeight={800} sx={{ color: "#0F172A" }}>
              Insights Log #{row.id}
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748B", fontWeight: 500 }}>
              {new Date(row.createdAt).toLocaleString()} · owner {row.ownerId} · {row.locale}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <StatusChip status={row.status} />
            {row.isAudio && (
              <Chip
                size="small"
                label="🎤 audio"
                sx={{
                  bgcolor: "#FCE7F3",
                  color: "#9D174D",
                  fontWeight: 700,
                  fontSize: 10,
                  height: 22,
                }}
              />
            )}
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Section title="User message" onCopy={() => copy(row.message)}>
          <Typography sx={{ whiteSpace: "pre-wrap", fontFamily: "monospace" }}>
            {row.message || "(empty)"}
          </Typography>
        </Section>

        <Section title={`Intent · confidence ${row.confidence?.toFixed(2) ?? "—"}`}>
          <Chip size="small" label={row.intent || "—"} sx={{ fontFamily: "monospace" }} />
        </Section>

        <Section title="Narration" onCopy={() => copy(row.narration || "")}>
          <Typography sx={{ whiteSpace: "pre-wrap" }}>
            {row.narration || "(empty)"}
          </Typography>
        </Section>

        <Section title="Classifier params (JSON)" onCopy={() => copy(row.params)}>
          <Pre>{JSON.stringify(row.params || {}, null, 2)}</Pre>
        </Section>

        <Section title="SQL handler result (JSON)" onCopy={() => copy(row.data)}>
          <Pre>{JSON.stringify(row.data || null, null, 2)}</Pre>
        </Section>

        {row.errorMessage && (
          <Section title="Error">
            <Typography color="error" sx={{ fontFamily: "monospace" }}>{row.errorMessage}</Typography>
          </Section>
        )}

        <Section title="Tokens / latency">
          <Stack direction="row" spacing={3} flexWrap="wrap">
            <Field label="Classifier in"  value={row.tokens?.classifierIn} />
            <Field label="Classifier out" value={row.tokens?.classifierOut} />
            <Field label="Narrator in"    value={row.tokens?.narratorIn} />
            <Field label="Narrator out"   value={row.tokens?.narratorOut} />
            <Field label="Total"          value={row.tokens?.total} />
            <Field label="Latency (ms)"   value={row.latencyMs} />
            <Field label="Model"          value={row.model || "—"} mono />
          </Stack>
        </Section>

        {row.isAudio && row.audio && (
          <Section title="Audio">
            <Stack direction="row" spacing={3} flexWrap="wrap">
              <Field label="Duration (s)" value={row.audio.durationSec} />
              <Field label="MIME"         value={row.audio.mimeType} mono />
              <Field label="Bytes"        value={row.audio.bytes?.toLocaleString()} />
            </Stack>
          </Section>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #E5E7EB" }}>
        <Button
          onClick={onClose}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Section({ title, children, onCopy }) {
  return (
    <Box sx={{ mb: 2.5 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 800,
            color: "#475569",
            textTransform: "uppercase",
            letterSpacing: 0.6,
            fontSize: 11,
          }}
        >
          {title}
        </Typography>
        {onCopy && (
          <IconButton size="small" onClick={onCopy} title="Copy">
            <ContentCopyIcon sx={{ fontSize: 16, color: "#94A3B8" }} />
          </IconButton>
        )}
      </Stack>
      {children}
      <Divider sx={{ mt: 1.25, borderColor: "#F1F5F9" }} />
    </Box>
  );
}

function Field({ label, value, mono }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
        {label}
      </Typography>
      <Typography sx={{ fontFamily: mono ? "monospace" : undefined, fontWeight: 700, color: "#0F172A" }}>
        {value == null || value === "" ? "—" : value}
      </Typography>
    </Box>
  );
}

function Pre({ children }) {
  return (
    <Box
      component="pre"
      sx={{
        bgcolor: "#0F172A",
        color: "#E2E8F0",
        borderRadius: 1.5,
        p: 1.5,
        fontSize: 11,
        overflow: "auto",
        maxHeight: 280,
        m: 0,
        fontFamily: '"Fira Code", "SF Mono", monospace',
        lineHeight: 1.5,
      }}
    >
      {children}
    </Box>
  );
}
